/**
 * Merci : le suivi de l'upsell de la fiche produit.
 *
 * Repris de `assets/custom.js` du thème actuel (lignes 479 à 692), à la demande
 * du rapport de suivi : sans ces données, le chiffre d'affaires de l'upsell
 * tombe à zéro dans le reporting. Deux choses, sur chaque ajout au panier
 * depuis l'upsell :
 *
 *   1. deux **propriétés de ligne** masquées (préfixe « _ ») —
 *      `_upsell_location` et `_upsell_source_product_variant_id` —, lues par
 *      l'API Admin pour retrouver le montant réel, remises déduites ;
 *   2. **après le succès** de l'ajout, et non au clic — un double clic ne doit
 *      pas compter deux fois —, l'événement `upsell_product_added_to_cart`,
 *      lu par le pixel personnalisé de la boutique. Son nom et ses champs sont
 *      un contrat avec ce pixel : ne rien renommer. Les identifiants partent en
 *      chaînes, le prix en nombre arrondi à deux décimales, comme sur le site
 *      actuel.
 *
 * --- Ce qu'est l'upsell ici
 *
 * Le bloc `product-recommendations` en mode *complémentaires*, posé dans la
 * colonne d'informations de la fiche : `blocks/product-recommendations.liquid`
 * marque son élément de `data-merci-upsell` et charge ce script. Ses deux
 * apparences sont couvertes — le rapport ne nommait que la « ligne », mais la
 * « carte » est l'apparence par défaut :
 *
 *   - **ligne** (`snippets/merci-upsell-row.liquid`) : un formulaire par ligne ;
 *   - **carte** : l'ajout rapide d'Horizon. Un produit à variante unique
 *     s'ajoute par le formulaire de la carte ; les autres ouvrent la fenêtre
 *     d'ajout rapide (`#quick-add-dialog`), **partagée** avec le reste de la
 *     page — dont l'upsell du tiroir panier. On retient donc quelle carte l'a
 *     ouverte : seul un ajout depuis la fenêtre ouverte par une carte de
 *     l'upsell compte.
 *
 * L'upsell du tiroir panier n'est **pas** suivi : nouveau, absent du site
 * actuel, il lui faudrait une `location` à lui, à convenir avec l'auteur du
 * rapport (point 51 des notes).
 *
 * --- Comment, sans toucher au formulaire d'Horizon
 *
 * `product-form-component` lit le formulaire par `FormData` au moment de
 * l'envoi. Un écouteur de `submit` posé sur `window` en **capture** passe
 * avant le sien — délégué sur `document` en phase de bouillonnement — et
 * glisse les deux champs dans le formulaire juste avant la lecture.
 *
 * Le succès se lit sur l'événement standard `shopify:cart:lines-update`, que
 * le formulaire émet avec une promesse : résolue sans `didError`, l'ajout a
 * réussi, et le panier rafraîchi qu'elle porte donne le prix de la variante.
 *
 * **Limite connue** : si l'on valide pendant qu'un changement de variante est
 * en cours dans la fenêtre d'ajout rapide, Horizon met l'ajout en file et
 * l'envoie plus tard en JSON, sans relire le formulaire. L'événement part bien,
 * mais les propriétés de ligne manquent. Il faut cliquer dans la fraction de
 * seconde où la variante se charge.
 */

const LOCATION = 'product_page';
const PROPERTY_ATTRIBUTE = 'data-merci-upsell-property';

/** La fenêtre d'ajout rapide ouverte depuis l'upsell : la zone d'upsell qui l'a ouverte. */
let quickAddOrigin = null;

/** Les envois d'upsell en cours, par formulaire, en attente de leur résultat. */
const pending = new WeakMap();

/** @param {Element} element */
function upsellRoot(element) {
  return element.closest('[data-merci-upsell]');
}

/**
 * La variante du produit principal **au moment de l'ajout** : le client a pu en
 * changer depuis le chargement. Lue dans le formulaire de la fiche, puis dans
 * l'adresse (`?variant=`, qu'Horizon tient à jour), puis celle rendue par le
 * serveur — pour un produit « sur commande », qui n'a pas de formulaire.
 *
 * @param {HTMLElement} root
 */
function sourceVariantId(root) {
  const productId = root.dataset.merciUpsellSourceProductId;
  const scope = root.closest('.shopify-section') || document;
  const mainForm = [...scope.querySelectorAll(`product-form-component[data-product-id="${productId}"]`)].find(
    (form) => !form.closest('[data-merci-upsell], #quick-add-dialog')
  );
  const fromForm = mainForm?.querySelector('[name="id"]')?.value;
  const fromUrl = new URL(window.location.href).searchParams.get('variant');

  return String(fromForm || fromUrl || root.dataset.merciUpsellSourceVariantId || '');
}

/**
 * Remplace les champs d'upsell du formulaire : retirés d'abord, toujours — la
 * fenêtre d'ajout rapide resservira peut-être pour un ajout qui n'en est pas un.
 *
 * @param {HTMLFormElement} form
 * @param {Record<string, string> | null} properties
 */
function setProperties(form, properties) {
  form.querySelectorAll(`[${PROPERTY_ATTRIBUTE}]`).forEach((input) => input.remove());
  if (!properties) return;

  for (const [name, value] of Object.entries(properties)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${name}]`;
    input.value = value;
    input.setAttribute(PROPERTY_ATTRIBUTE, '');
    form.append(input);
  }
}

// Le bouton d'ajout rapide d'une carte : on retient s'il ouvre la fenêtre
// depuis l'upsell. Tout autre bouton d'ajout rapide efface ce souvenir.
window.addEventListener(
  'click',
  (event) => {
    const quickAdd = event.target instanceof Element ? event.target.closest('quick-add-component') : null;
    if (!quickAdd) return;

    const root = upsellRoot(quickAdd);
    quickAddOrigin = root ? { root } : null;
  },
  { capture: true }
);

window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const component = form.closest('product-form-component');
    if (!(component instanceof HTMLElement)) return;

    // Depuis la fenêtre d'ajout rapide ouverte par l'upsell, le produit peut
    // avoir changé : une autre couleur est un autre produit, et la fenêtre se
    // recharge sur elle (`assets/merci-quick-add.js`). L'ajout reste un ajout
    // d'upsell, quel que soit le produit — d'où l'absence de comparaison avec
    // celui de la carte.
    let root = upsellRoot(component);
    if (!root && quickAddOrigin && component.closest('#quick-add-dialog')) {
      root = quickAddOrigin.root;
    }

    if (!root) {
      setProperties(form, null);
      pending.delete(component);
      return;
    }

    const source = sourceVariantId(root);
    setProperties(form, {
      _upsell_location: LOCATION,
      _upsell_source_product_variant_id: source,
    });

    pending.set(component, {
      sourceProductId: String(root.dataset.merciUpsellSourceProductId || ''),
      sourceVariantId: source,
      targetProductId: String(component.dataset.productId || ''),
      targetVariantId: String(new FormData(form).get('id') || ''),
    });
  },
  { capture: true }
);

/** `gid://shopify/ProductVariant/123` comme `123` donnent `123`. */
function numericId(value) {
  return String(value ?? '').split('/').pop() || '';
}

document.addEventListener('shopify:cart:lines-update', (event) => {
  const component = event.target;
  const snapshot = pending.get(component);
  if (!snapshot) return;
  pending.delete(component);

  // La variante réellement envoyée : celle de l'événement, sinon celle du
  // formulaire au moment de l'envoi.
  const targetVariantId = numericId(event.lines?.[0]?.merchandiseId) || snapshot.targetVariantId;

  event.promise
    ?.then((result) => {
      if (!result || result.detail?.didError) return;

      const item = result.detail?.items?.find((line) => String(line.variant_id) === targetVariantId);
      const cents = item ? item.original_price ?? item.price : null;
      const price = cents == null ? null : Math.round(cents) / 100;
      const currency = result.cart?.cost?.totalAmount?.currencyCode || window.Shopify?.currency?.active || '';

      window.Shopify?.analytics?.publish?.('upsell_product_added_to_cart', {
        location: LOCATION,
        source_product_id: snapshot.sourceProductId,
        source_product_variant_id: snapshot.sourceVariantId,
        target_product_id: snapshot.targetProductId,
        target_product_variant_id: targetVariantId,
        target_product_variant_price: price,
        target_product_variant_currency: currency,
      });
    })
    .catch(() => {});
});
